import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {FeatureStatusCard} from 'shells/card/feature-status-card';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {colors} from 'shells/colors/colors';
import {ErrorState} from 'shells/error-state/error-state';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FooterWithControls, RightFrame, ModalMain} from 'shells/modal/step-modal/layout';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {Body} from 'shells/typography/body';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalSubheader} from 'shells/typography/modal-subheader';

import {
    getServiceNoun,
    getServiceTypeFromString,
    getServiceTypeIconComponent,
} from '../../calls/helpers';
import {useGetSessionUser} from '../../core/use-get-session-user';
import {isUserAutoLogEnabled} from '../../external-meetings/helpers';
import {TranscriptionModePicker} from '../../external-meetings/transcription-mode-picker';

import {PhoneIntegrationDefaultProviderSetting} from './phone-integration-default-provider-setting';

type Props = {
    goBack: () => void;
    nextPage: string;
    search: string;
};

export function SyncPreferences(props: Props) {
    const [shouldAutolog, setShouldAutolog] = React.useState<string | null | undefined>(null);

    const {user, isLoading: isLoadingUser} = useGetSessionUser();

    const integrationTypeQueryParam =
        new URLSearchParams(props.search).get('integrationType') || '';
    const integrationType = getServiceTypeFromString(integrationTypeQueryParam);

    const phoneIntegrations = user ? user.phoneIntegrations : [];
    const enabledPhoneIntegrations = phoneIntegrations.filter(
        (integration) => integration.isEnabledForUser
    );
    const clickToCallIntegration = user ? user.clickToCallIntegration : null;

    const integrationBeingAdded = enabledPhoneIntegrations.find(
        (phoneIntegration) => phoneIntegration.integrationType === integrationType
    );

    React.useEffect(() => {
        setShouldAutolog(
            integrationType && user && isUserAutoLogEnabled(integrationType, user) ? '1' : '0'
        );
    }, [user, integrationType]);

    React.useEffect(() => {
        window.dispatchEvent(new Event('onboarding-step-completed'));
    }, []);

    const didGraphQLRequestsFail =
        (!isLoadingUser && !integrationBeingAdded) ||
        (!isLoadingUser && !clickToCallIntegration) ||
        (!isLoadingUser && !user);

    // Integration type query param may be missing
    // GraphQL requests can fail(rarely)
    if (!integrationType || didGraphQLRequestsFail) {
        return (
            <RightFrame>
                <ErrorState errorMessage='Something went wrong. Please try again.' />
            </RightFrame>
        );
    }

    // Data we need for component is not available yet
    if (isLoadingUser || !user || !integrationBeingAdded || !clickToCallIntegration) {
        return (
            <RightFrame>
                <LoadingPage size={64} fill={colors.blueLt} />
            </RightFrame>
        );
    }

    const serviceNoun = getServiceNoun(integrationType);

    return (
        <RightFrame>
            <ModalMain>
                <SpacerGroup mb={32}>
                    <ModalHeader>Great, you’re connected!</ModalHeader>
                    <ModalSubheader>
                        Your completed {serviceNoun} calls will automatically sync to Nutshell.
                    </ModalSubheader>
                    <FeatureStatusCard
                        icon={getServiceTypeIconComponent(integrationType)}
                        statusBadge={{
                            text: 'Connected',
                            level: 'success',
                        }}
                        title={serviceNoun}
                        subtitle={
                            <Body color='dark'>{integrationBeingAdded.externalUserIdentifier}</Body>
                        }
                        tertiaryData={<Body>{integrationBeingAdded.tertiaryInformation}</Body>}
                    />
                </SpacerGroup>
                <SpacerGroup mb={32}>
                    <TranscriptionModePicker
                        serviceType={integrationType}
                        sendAutologValueToParent={(newVal: string) => setShouldAutolog(newVal)}
                        user={user}
                    />
                </SpacerGroup>
                <PhoneIntegrationDefaultProviderSetting
                    enabledPhoneIntegrations={enabledPhoneIntegrations}
                    clickToCallEnabled={clickToCallIntegration.isEnabled}
                    showSavedToast={true}
                    onError={() => {
                        // TODO: need some kind of error state?
                    }}
                    radioGroupName='configure default provider - modal'
                />
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={0} slideCount={3} />}
                buttons={
                    <React.Fragment>
                        <Button variant='text' onClick={props.goBack}>
                            Back
                        </Button>
                        <Link
                            variant='button-primary'
                            size='big'
                            // @ts-expect-error
                            as={RoutedLink}
                            to={
                                shouldAutolog
                                    ? `${props.nextPage}&shouldAutolog=${shouldAutolog}`
                                    : props.nextPage
                            }
                        >
                            Next
                        </Link>
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}
